import React from 'react';
import { Globe, MapPin, Plane, TrendingUp, Users, CheckCircle, FileText } from 'lucide-react';
import { programasSociales } from '../mock';

const iconMap = {
  Globe: Globe,
  MapPin: MapPin,
  Plane: Plane,
  TrendingUp: TrendingUp,
  Users: Users
};

const Programas = () => {
  const programaDetalle = [
    {
      id: 1,
      nombre: 'Yucatán sin Fronteras',
      descripcion: 'Programa integral de movilidad laboral internacional que conecta el talento yucateco con oportunidades globales de empleo.',
      icono: 'Globe',
      beneficios: [
        'Vinculación con empleadores internacionales',
        'Asesoría para trámites migratorios',
        'Capacitación en idiomas y habilidades interculturales',
        'Seguimiento y apoyo durante el proceso'
      ],
      requisitos: [
        'Ser mayor de 18 años',
        'Contar con experiencia laboral comprobable',
        'Disponibilidad para movilidad internacional',
        'Documentación legal vigente'
      ]
    },
    {
      id: 2,
      nombre: 'Yucatán en México',
      descripcion: 'Fortalecimiento de vínculos comerciales y laborales a nivel nacional para empresas y profesionistas yucatecos.',
      icono: 'MapPin',
      beneficios: [
        'Red de contactos con empresas nacionales',
        'Participación en ferias y eventos comerciales',
        'Apoyo para expansión a otros estados',
        'Capacitación en comercio nacional'
      ],
      requisitos: [
        'Empresa registrada en Yucatán',
        'Al menos 1 año de operación',
        'Plan de expansión documentado',
        'Estar al corriente con obligaciones fiscales'
      ]
    },
    {
      id: 3,
      nombre: 'Yucatán en el Mundo',
      descripcion: 'Promoción internacional de productos y servicios yucatecos en mercados globales.',
      icono: 'Plane',
      beneficios: [
        'Acceso a mercados internacionales',
        'Apoyo en certificaciones de exportación',
        'Misión comercial internacional',
        'Asesoría en logística de exportación'
      ],
      requisitos: [
        'Producto o servicio exportable',
        'Capacidad de producción escalable',
        'Certificaciones de calidad',
        'Plan de negocios internacional'
      ]
    },
    {
      id: 4,
      nombre: 'Impulsa tu Crecimiento',
      descripcion: 'Capacitación empresarial y apoyo financiero para emprendedores y PYMES del estado.',
      icono: 'TrendingUp',
      beneficios: [
        'Financiamiento hasta $500,000 MXN',
        'Capacitación empresarial especializada',
        'Mentoría personalizada',
        'Acceso a red de inversionistas'
      ],
      requisitos: [
        'Proyecto de negocio viable',
        'Radicar en Yucatán',
        'Plan financiero detallado',
        'Edad entre 18 y 65 años'
      ]
    },
    {
      id: 5,
      nombre: 'Profesionistas de Cooperativa',
      descripcion: 'Desarrollo de cooperativas profesionales para fomentar el trabajo colaborativo y sostenible.',
      icono: 'Users',
      beneficios: [
        'Asesoría legal para constitución',
        'Capacitación en modelo cooperativista',
        'Apoyo financiero inicial',
        'Seguimiento y acompañamiento'
      ],
      requisitos: [
        'Mínimo 5 socios fundadores',
        'Profesionistas titulados',
        'Proyecto cooperativo estructurado',
        'Compromiso de trabajo colaborativo'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1920&q=80" 
            alt="Programas Sociales"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h1 className="text-5xl font-bold text-white mb-4">Programas Sociales</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Descubre todos los programas disponibles y encuentra el apoyo que mejor se adapte a tus necesidades laborales y empresariales.
          </p>
        </div>
      </div>

      {/* Programs Detail Section */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-12">
          {programaDetalle.map((programa, index) => {
            const IconComponent = iconMap[programa.icono];
            return (
              <div
                key={programa.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3">
                  {/* Left Section - Info */}
                  <div className="lg:col-span-2 p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                          {programa.nombre}
                        </h2>
                        <p className="text-gray-600">
                          {programa.descripcion}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          Beneficios
                        </h3>
                        <ul className="space-y-2">
                          {programa.beneficios.map((beneficio, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0"></span>
                              <span>{beneficio}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <FileText className="h-5 w-5 text-gray-600" />
                          Requisitos
                        </h3>
                        <ul className="space-y-2">
                          {programa.requisitos.map((requisito, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0"></span>
                              <span>{requisito}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - CTA */}
                  <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-8 flex flex-col justify-center items-center text-center border-l border-gray-200">
                    <div className="mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#8B1538] to-[#6B0F2A] rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                        <IconComponent className="h-10 w-10 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        ¿Interesado?
                      </h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Obtén más información y comienza tu proceso de inscripción
                      </p>
                    </div>
                    <button className="w-full px-6 py-3 bg-[#8B1538] text-white rounded-lg hover:bg-[#6B0F2A] transition-colors font-medium">
                      Solicitar Información
                    </button>
                    <button className="w-full mt-3 px-6 py-3 bg-white text-[#8B1538] border-2 border-[#8B1538] rounded-lg hover:bg-[#8B1538] hover:text-white transition-colors font-medium">
                      Descargar Convocatoria
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Programas;